import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/auth';
import { useLanguage } from '@/context/language-context';
import { supabase } from '@/integrations/supabase/client';
import { useMessages } from '@/hooks/useMessages';
import { useConversations, Conversation } from '@/hooks/useConversations';
import { useIsMobile } from '@/hooks/use-mobile';
import MessagesMobileView from '@/components/messages/MessagesMobileView';
import MessagesDesktopView from '@/components/messages/MessagesDesktopView';
import { sendBookingRequest as sendBookingRequestService } from '@/services/bookingService';
import BookingDialog from '@/components/messages/BookingDialog';

interface PartnerDetails {
  id: string;
  display_name?: string;
  role?: string;
  avatar_url?: string | null;
  is_online?: boolean;
  is_verified?: boolean;
  age?: number;
  city?: string;
  nationality?: string;
}

const Messages = () => {
  const { conversationId: activeConversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { getUrlWithLanguage } = useLanguage();
  const isMobile = useIsMobile();

  const { 
    conversations, 
    isLoading: isLoadingConversations,
    fetchConversations,
    archiveConversation,
    markConversationAsRead
  } = useConversations();

  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [partnerDetails, setPartnerDetails] = useState<PartnerDetails | null>(null);
  const [isLoadingPartner, setIsLoadingPartner] = useState(false);
  const [partnerError, setPartnerError] = useState<string | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  
  useEffect(() => {
    if (activeConversationId) {
      const foundConv = conversations.find(c => c.id === activeConversationId);
      setActiveConversation(foundConv || null);
      if (foundConv && foundConv.unreadCount > 0) {
          console.log(`[Messages Page] Conversation ${activeConversationId} activated with ${foundConv.unreadCount} unread. Marking as read.`);
          markConversationAsRead(activeConversationId);
      }
    } else {
      setActiveConversation(null);
    }
  }, [activeConversationId, conversations, markConversationAsRead]);

  useEffect(() => {
    const fetchPartner = async () => {
      if (!activeConversation || !user) {
        setPartnerDetails(null);
        return;
      }
      
      const partnerId = activeConversation.participant1_id === user.id 
        ? activeConversation.participant2_id 
        : activeConversation.participant1_id;
        
      if (!partnerId) {
        setPartnerError("Impossible de déterminer le partenaire.");
        setPartnerDetails(null);
        return;
      }

      setIsLoadingPartner(true);
      setPartnerError(null);
      setPartnerDetails(null);

      try {
        console.log(`[Messages] Fetching partner details for ID: ${partnerId}`);
        let fetchedPartner: PartnerDetails | null = null;

        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, display_name, role, avatar_url, is_verified')
          .eq('id', partnerId)
          .maybeSingle();
          
        if (profileError) console.warn(`[Messages] Profile fetch error: ${profileError.message}`);
        
        if (profileData) {
          fetchedPartner = { ...profileData, is_online: false };
        } else {
           const { data: escortData, error: escortError } = await supabase
            .from('escort_profiles')
            .select('id, display_name, is_online, is_verified, age, city, nationality')
            .eq('id', partnerId)
            .maybeSingle();
            
          if (escortError) console.warn(`[Messages] Escort profile fetch error: ${escortError.message}`);
          
          if (escortData) {
            fetchedPartner = {
              id: escortData.id,
              display_name: escortData.display_name,
              role: 'escort',
              is_online: escortData.is_online,
              is_verified: escortData.is_verified,
              avatar_url: null,
              age: escortData.age,
              city: escortData.city,
              nationality: escortData.nationality,
            };
          } 
        }
        
        if (!fetchedPartner) {
           throw new Error(`Partner details not found for ID: ${partnerId}`);
        }
        
        if (!fetchedPartner.avatar_url && fetchedPartner.role === 'escort') {
            console.log(`[Messages] Attempting to fetch main photo for escort ID: ${fetchedPartner.id}`); 
            const { data: photoData, error: photoError } = await supabase
              .from('escort_photos')
              .select('photo_url')
              .eq('escort_id', fetchedPartner.id)
              .eq('is_main', true)
              .maybeSingle();
              
            if (photoError) {
                 console.warn(`[Messages] Error fetching main photo path: ${photoError.message}`);
            }
            if (!photoError && photoData && photoData.photo_url) { 
                const filePath = photoData.photo_url;
                console.log(`[Messages] Main photo path found: ${filePath}`);
                const { data: urlData } = supabase
                  .storage
                  .from('escort-photos')
                  .getPublicUrl(filePath);
                
                if (urlData && urlData.publicUrl) {
                     console.log(`[Messages] Constructed public URL: ${urlData.publicUrl}`);
                     fetchedPartner.avatar_url = urlData.publicUrl;
                } else {
                    console.warn(`[Messages] Could not get public URL for path: ${filePath}`);
                }
            } else if (!photoData && !photoError) {
                 console.log(`[Messages] No main photo found for escort.`);
            }
        }
        
        if(fetchedPartner.role !== 'escort' || fetchedPartner.is_online === null || fetchedPartner.is_online === undefined) {
             fetchedPartner.is_online = false;
        }
        
        setPartnerDetails(fetchedPartner);

      } catch (error: any) {
         console.error('[Messages] Error fetching partner:', error);
         setPartnerError(error.message || "Erreur lors du chargement du partenaire.");
      } finally {
         setIsLoadingPartner(false);
      }
    };

    fetchPartner();
  }, [activeConversation, user]);

  const { 
    messages, 
    isLoading: isLoadingMessages, 
    sendMessage,
    deleteMessage 
  } = useMessages(activeConversationId, partnerDetails?.id);
  
  const handleSelectConversation = (conversationId: string) => {
     navigate(getUrlWithLanguage(`/messages/${conversationId}`));
  };

  const handleSendBookingRequest = async (selectedDate: Date, timeSlot: string, duration: number = 60) => {
    if (!selectedDate || !timeSlot || !partnerDetails?.id || !user || !activeConversationId) {
       toast({ title: 'Erreur', description: 'Informations manquantes pour la réservation.', variant: 'destructive' });
       return;
    }
    try {
        await sendBookingRequestService(user.id, partnerDetails.id, selectedDate, timeSlot, duration);
        
        const messageContent = `Demande de rendez-vous pour le ${selectedDate.toLocaleDateString('fr-FR', { 
           day: 'numeric', month: 'long', year: 'numeric' 
         })} à ${timeSlot} (durée: ${duration} minutes)`;
        await sendMessage(messageContent);
        
        toast({ title: 'Demande envoyée', description: 'Votre demande de réservation a été envoyée.' });
    } catch (error) {
        console.error('Error sending booking request:', error);
        toast({ title: 'Erreur', description: 'Impossible d\'envoyer la demande de réservation.', variant: 'destructive' });
    }
  };

  const handleArchiveConversation = async (conversationId: string) => {
    try {
      await archiveConversation(conversationId);
      // Si la conversation active est celle qui vient d'être archivée, revenir à la liste
      if (activeConversationId === conversationId) {
        navigate(getUrlWithLanguage('/messages'));
      }
    } catch (error) {
      console.error('Error archiving conversation:', error);
      toast({ 
        title: 'Erreur', 
        description: 'Impossible d\'archiver la conversation',
        variant: 'destructive'
      });
    }
  };

  console.log("[Messages] Rendering View. isLoadingConversations:", isLoadingConversations, "conversations count:", conversations.length);
  if (!isLoadingConversations) {
     console.log("[Messages] Conversations Data:", conversations);
  }

  return (
    <Layout>
      {isMobile ? (
        <MessagesMobileView
          conversations={conversations}
          activeConversation={activeConversation}
          partnerDetails={partnerDetails}
          messages={messages}
          isLoadingConversations={isLoadingConversations}
          isLoadingMessages={isLoadingMessages || isLoadingPartner}
          onSelectConversation={(conversationId) => {
            handleSelectConversation(conversationId);
          }}
          sendMessage={sendMessage}
          deleteMessage={deleteMessage}
          onArchiveConversation={handleArchiveConversation}
          userId={user?.id}
          handleBookAppointment={handleSendBookingRequest}
          isBookingDialogOpen={isBookingModalOpen}
          setIsBookingDialogOpen={setIsBookingModalOpen}
          chatPartner={partnerDetails && {
            id: partnerDetails.id,
            name: partnerDetails.display_name || 'Utilisateur',
            avatar: partnerDetails.avatar_url,
            type: partnerDetails.role === 'escort' ? 'escort' : 'client',
            isVerified: partnerDetails.is_verified
          }}
          handleBackToList={() => handleSelectConversation('')}
        />
      ) : (
        <MessagesDesktopView
          conversations={conversations}
          activeConversation={activeConversation}
          partnerDetails={partnerDetails}
          messages={messages}
          isLoadingConversations={isLoadingConversations}
          isLoadingMessages={isLoadingMessages || isLoadingPartner}
          onSelectConversation={handleSelectConversation}
          onSendMessage={sendMessage}
          deleteMessage={deleteMessage}
          onArchiveConversation={handleArchiveConversation}
          userId={user?.id}
          onSendBookingRequest={handleSendBookingRequest}
          isBookingModalOpen={isBookingModalOpen}
          setIsBookingModalOpen={setIsBookingModalOpen}
        />
      )}
    </Layout>
  );
};

export default Messages;
